/* eslint-disable eqeqeq */
// @flow
import * as React from 'react';
import idx from 'idx';
import { isEqual, isNil } from 'lodash';
import { Query } from 'react-apollo';

import AdminNewAutoMessageComposeDistrictSender from './AdminNewAutoMessageComposeDistrictSender';
import AdminNewAutoMessageComposeIndividualSender from './AdminNewAutoMessageComposeIndividualSender';
import AdminNewAutoMessageComposeMessageName from './AdminNewAutoMessageComposeMessageName';
import AdminNewAutoMessageComposeRecipients from './AdminNewAutoMessageComposeRecipients';
import AdminNewAutoMessageComposeTextarea from './AdminNewAutoMessageComposeTextarea';
import AdminNewAutoMessageComposeUrgency from './AdminNewAutoMessageComposeUrgency';
import Button from 'components/Button';
import Container from 'components/Container';
import CurrentUser from 'auth/CurrentUser';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import Heading from 'components/Heading';
import Row from 'components/Row';
import Text from 'components/Text';
import View from 'components/View';
import WizardPageLayout from 'components/WizardPageLayout';

import URGENT_MESSAGING_PERMISSION_QUERY from 'chat/queries/UrgentMessagingPermissionQuery.gql';

import type {
  GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_messageContent,
  GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_sender,
  GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_organization,
} from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageWithoutSends';
import type {
  AdminNewAutoMessageOrgs_organizations,
  SaveAutoMessageInput,
  AutoMessageContentInput,
  AutoMessageSenderInput,
} from './queries/__generated__/AdminNewAutoMessageOrgs';
import type { ApolloError } from 'apollo-server-errors';
import type { UrgentMessagingPermission } from 'chat/queries/__generated__/UrgentMessagingPermission';

export type Props = {
  messageName: ?string,
  organization: GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_organization,
  sender: ?GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_sender,
  includeParents: boolean,
  includeStudents: boolean,
  messageContent: ?GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_messageContent,
  customMessageFields: ?$ReadOnlyArray<string>,
  potentialOrgs: AdminNewAutoMessageOrgs_organizations[],
  onChangeField: () => void,
  onResetFieldChange: () => void,
  onAdvance: () => void,
  onBack: () => void,
  onSave: (fields: SaveAutoMessageInput, onSuccess: Function) => void,
  hasCustomizedMessaging: boolean,
  hasUrgentMessaging: boolean,
  isUrgent: boolean,
};

type State = {
  messageNameFromField: ?string,
  orgUuidFromField: ?string,
  senderFromField: ?AutoMessageSenderInput,
  includeParentsFromField: boolean,
  includeStudentsFromField: boolean,
  messageContentIsValid: boolean,
  messageContentFromField: ?AutoMessageContentInput,
  customMessageFieldsFromField: ?($ReadOnlyArray<string> | string[]),
  isUrgentFromField: boolean,
};

const eventPrefix = 'auto_messaging.compose_message';

class AdminNewAutoMessageCompose extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.messageContentTextarea = React.createRef();
    this.state = {
      messageNameFromField: props.messageName,
      orgUuidFromField: props.organization.uuid,
      senderFromField: props.sender && {
        uuid: props.sender.uuid,
        type: props.sender.__typename === 'UserSender' ? 'user' : 'district',
      },
      includeParentsFromField: props.includeParents,
      includeStudentsFromField: props.includeStudents,
      messageContentFromField: props.messageContent && {
        body: props.messageContent.body,
        fileAttachmentUuids: props.messageContent.fileAttachmentUuids,
      },
      messageContentIsValid: Boolean(props.messageContent && props.messageContent.body),
      customMessageFieldsFromField: props.customMessageFields,
      isUrgentFromField: props.isUrgent,
    };
  }

  componentDidMount() {
    this.trackEvent('view');
  }

  trackEvent(event: string) {
    EventTracking.trackKey(`${eventPrefix}.${event}`);
  }

  // TODO: fix type; createRef is not currently well-defined in flow
  messageContentTextarea: { current: any };

  renderAnnouncementOptions = () => {
    return (
      <Query query={URGENT_MESSAGING_PERMISSION_QUERY}>
        {({
          loading,
          error,
          data,
        }: {
          loading: boolean,
          error: ApolloError,
          data: UrgentMessagingPermission,
        }) => {
          if (loading || !data) {
            return null;
          }
          if (error) {
            return (
              <ErrorReporter error="AdminNewAutoMessageCompose - could not fetch urgent messaging permissions" />
            );
          }

          const currentUser = CurrentUser.getUser();
          const webPermission =
            currentUser && currentUser.hasFeature('urgent_messaging.web.apr-19');

          if (webPermission && data.me && data.me.canSendUrgentMessage) {
            return (
              <AdminNewAutoMessageComposeUrgency
                data-test="urgency-radio"
                handleIsUrgentMessageChange={this.handleIsUrgentMessageChange}
                isUrgent={this.state.isUrgentFromField}
              />
            );
          }
          return null;
        }}
      </Query>
    );
  };

  checkAndHandleFieldChanges = () => {
    const senderFromFieldUuid = idx(this.state.senderFromField, _ => _.uuid);
    const senderUuid = idx(this.props.sender, _ => _.uuid);
    const messageContentFromFieldBody = idx(this.state.messageContentFromField, _ => _.body);
    const messageContentBody = idx(this.props.messageContent, _ => _.body);

    const fieldsAreUnchanged =
      isEqual(this.state.messageNameFromField, this.props.messageName) &&
      // use isNil to stomp undefined vs null
      // if both values are nil, then nothing has changed.
      // if they aren't, check if they're equal
      ((isNil(senderFromFieldUuid) && isNil(senderUuid)) ||
        isEqual(senderFromFieldUuid, senderUuid)) &&
      isEqual(this.state.includeParentsFromField, this.props.includeParents) &&
      isEqual(this.state.includeStudentsFromField, this.props.includeStudents) &&
      ((isNil(messageContentFromFieldBody) && isNil(messageContentBody)) ||
        isEqual(messageContentFromFieldBody, messageContentBody)) &&
      isEqual(this.state.customMessageFieldsFromField, this.props.customMessageFields) &&
      isEqual(this.state.isUrgentFromField, this.props.isUrgent);

    if (fieldsAreUnchanged) {
      this.props.onResetFieldChange();
      return;
    }

    this.props.onChangeField();
  };

  handleIsUrgentMessageChange = (isUrgent: boolean) => {
    this.setState({ isUrgentFromField: isUrgent }, () => this.checkAndHandleFieldChanges());
  };

  handleMessageNameChange = (messageName: ?string) => {
    this.setState({ messageNameFromField: messageName }, () => this.checkAndHandleFieldChanges());
  };

  handleSenderChange = (sender: ?AutoMessageSenderInput) => {
    this.setState({ senderFromField: sender }, () => this.checkAndHandleFieldChanges());
  };

  handleIncludeParentsChange = (includeParents: boolean) => {
    this.setState({ includeParentsFromField: includeParents }, () =>
      this.checkAndHandleFieldChanges()
    );
  };

  handleIncludeStudentsChange = (includeStudents: boolean) => {
    this.setState({ includeStudentsFromField: includeStudents }, () =>
      this.checkAndHandleFieldChanges()
    );
  };

  handleMessageContentChange = (
    messageContent: ?$Shape<AutoMessageContentInput>,
    valid: boolean
  ) => {
    this.setState(
      {
        // since the messageContent param that's passed into this method doesn't necessarily
        // contain every field, we should default to the existing fields and then only
        // override the ones that are explicitly being passed in.
        messageContentFromField: { ...this.state.messageContentFromField, ...messageContent },
        messageContentIsValid: valid,
      },
      () => this.checkAndHandleFieldChanges()
    );
  };

  handleCustomMessageFieldsChange = (customMessageFields: string[]) => {
    this.setState({ customMessageFieldsFromField: customMessageFields }, () =>
      this.checkAndHandleFieldChanges()
    );
  };

  handleContinue = () => {
    this.props.onSave(
      {
        messageName: this.state.messageNameFromField,
        orgUuid: this.state.orgUuidFromField,
        includeParents: this.state.includeParentsFromField,
        includeStudents: this.state.includeStudentsFromField,
        messageContent: this.state.messageContentFromField,
        sender: this.state.senderFromField,
        customMessageFields: this.state.customMessageFieldsFromField,
        isUrgent: this.state.isUrgentFromField,
      },
      this.props.onAdvance
    );
  };

  shouldDisableContinueButton = () => {
    return (
      !this.state.messageNameFromField ||
      !this.state.orgUuidFromField ||
      !this.state.messageContentFromField ||
      !this.state.messageContentIsValid ||
      (!this.state.includeParentsFromField && !this.state.includeStudentsFromField) ||
      !this.state.senderFromField
    );
  };

  selectDistrictSender = (orgUuid: string) => {
    this.handleSenderChange({ uuid: orgUuid, type: 'district' });
  };

  render() {
    const { potentialOrgs } = this.props;
    const { senderFromField, messageContentFromField } = this.state;
    const selectedOrg = potentialOrgs.find(org => org.uuid === this.state.orgUuidFromField);

    return (
      <WizardPageLayout
        backButton={
          <Button level="secondary" onClick={this.props.onBack}>
            Back
          </Button>
        }
        actionButtons={[
          <Button
            data-test="continue-button"
            onClick={this.handleContinue}
            disabled={this.shouldDisableContinueButton()}>
            Save and continue
          </Button>,
        ]}>
        <View>
          <Row size="xlarge">
            <Container flush size="large">
              <Text align="left" lineHeight="large">
                <Row size="small">
                  <Heading level="2">
                    <Text weight="semibold">Compose auto message</Text>
                  </Heading>
                </Row>
                <Container flush size="medium" align="left">
                  {this.renderAnnouncementOptions()}
                  <AdminNewAutoMessageComposeMessageName
                    data-test="message-name-input"
                    handleMessageNameChange={this.handleMessageNameChange}
                    defaultValue={this.state.messageNameFromField}
                  />
                  {selectedOrg && selectedOrg.type === 'District' ? (
                    <AdminNewAutoMessageComposeDistrictSender
                      orgName={selectedOrg.name}
                      onMount={() => this.selectDistrictSender(selectedOrg.uuid)}
                    />
                  ) : (
                    <AdminNewAutoMessageComposeIndividualSender
                      data-test="sender-input"
                      admins={selectedOrg ? selectedOrg.admins : []}
                      value={senderFromField && senderFromField.uuid}
                      handleSenderChange={this.handleSenderChange}
                    />
                  )}
                  <AdminNewAutoMessageComposeRecipients
                    data-test="recipient-checkboxes"
                    handleIncludeStudentsChange={this.handleIncludeStudentsChange}
                    handleIncludeParentsChange={this.handleIncludeParentsChange}
                    includeStudents={this.state.includeStudentsFromField}
                    includeParents={this.state.includeParentsFromField}
                  />
                  <AdminNewAutoMessageComposeTextarea
                    data-test="message-content-textarea"
                    hasCustomizedMessaging={this.props.hasCustomizedMessaging}
                    handleMessageContentChange={this.handleMessageContentChange}
                    handleCustomMessageFieldsChange={this.handleCustomMessageFieldsChange}
                    onTokenIncrease={() => this.trackEvent('custom_message_field.insert')}
                    value={messageContentFromField && messageContentFromField.body}
                    fileUuid={idx(messageContentFromField, _ => _.fileAttachmentUuids[0])}
                    isUrgent={this.state.isUrgentFromField}
                  />
                </Container>
              </Text>
            </Container>
          </Row>
        </View>
      </WizardPageLayout>
    );
  }
}

export default AdminNewAutoMessageCompose;
